import {useLocalStorage} from '@wandb/weave/util/useLocalStorage';
import {useCallback, useEffect, useMemo} from 'react';

import {trackWorkspaceBanners} from '../../services/analytics/workspaceBannersEvents';
import {useViewer} from '../../state/viewer/hooks';
import {type LoadableView} from '../../state/views/types';
import {isOwnView} from '../../state/views/util';
import {getNamedWorkspaceIdForView} from '../../state/workspaces/names';
import {
  useNwPermissions,
  viewerCanWriteToNw,
} from '../../state/workspaces/nwPermissions';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {OLD_CLI_WARNING_CUTOFF, YANKED_SDK_VERSION} from './constants';
import {type WorkspaceBannerType} from './types';

export function useIsSaveWorkspaceAllowed(view: LoadableView): boolean {
  const viewer = useViewer();
  const isOwnWorkspace = isOwnView(viewer, view);
  const nwPermissions = useNwPermissions();
  const nwId = getNamedWorkspaceIdForView(view.name, view.user);
  const isOwnPersonalWorkspace =
    isOwnWorkspace && nwId.nwType === 'personal workspace';
  const isWritableSharedView =
    nwId.nwType === 'shared workspace' &&
    viewerCanWriteToNw(nwPermissions.data);
  return isOwnPersonalWorkspace || isWritableSharedView;
}

export const useWorkspaceBanners = () => {
  const [isCliVersionWarningDismissed, setIsCliVersionWarningDismissed] =
    useLocalStorage('wb_ws_banner/cli_version_warning/dismissed_at', false);

  const [isYankedSdkWarningDismissed, setIsYankedSdkWarningDismissed] =
    useLocalStorage('wb_ws_banner/yanked_sdk_warning/dismissed_at', false);

  const dismissWorkspaceBanner = useCallback(
    (bannerType: WorkspaceBannerType) => {
      if (bannerType === 'cli-version-warning') {
        setIsCliVersionWarningDismissed(true);
      } else if (bannerType === 'yanked-sdk-warning') {
        setIsYankedSdkWarningDismissed(true);
      }
      trackWorkspaceBanners({
        bannerType,
        event: 'Banner dismissed',
      });
    },
    [setIsCliVersionWarningDismissed, setIsYankedSdkWarningDismissed]
  );

  /* --- CLI version warning --- */
  const {run} = useSingleRunModeContext();
  const shouldShowCliVersionWarning = useMemo(() => {
    if (!run || isCliVersionWarningDismissed) {
      return false;
    }
    const cliVersion = run._wandb['value.cli_version'];
    const createdAt = new Date(run.createdAt).valueOf();
    const isUsingOldCliVersion =
      createdAt >= OLD_CLI_WARNING_CUTOFF &&
      typeof cliVersion === 'string' &&
      /^0\.[89](\..+)?$/.test(cliVersion);
    return isUsingOldCliVersion;
  }, [isCliVersionWarningDismissed, run]);

  useEffect(() => {
    if (shouldShowCliVersionWarning) {
      trackWorkspaceBanners({
        bannerType: 'cli-version-warning',
        event: 'Banner displayed',
      });
    }
  }, [shouldShowCliVersionWarning]);

  /* --- Yanked SDK warning --- */
  const shouldShowYankedSdkWarning = useMemo(() => {
    if (!run || isYankedSdkWarningDismissed) {
      return false;
    }
    const cliVersion = run._wandb['value.cli_version'];
    return cliVersion === YANKED_SDK_VERSION;
  }, [isYankedSdkWarningDismissed, run]);

  useEffect(() => {
    if (shouldShowYankedSdkWarning) {
      trackWorkspaceBanners({
        bannerType: 'yanked-sdk-warning',
        event: 'Banner displayed',
      });
    }
  }, [shouldShowYankedSdkWarning]);

  /* --- Return value --- */
  return useMemo(() => {
    return {
      dismissWorkspaceBanner,
      shouldShowCliVersionWarning,
      shouldShowYankedSdkWarning,
    };
  }, [
    dismissWorkspaceBanner,
    shouldShowCliVersionWarning,
    shouldShowYankedSdkWarning,
  ]);
};
